/**
 * Vanilla confirmation modal: focus trap, ESC, overlay dismiss, body scroll lock.
 * Exposes window.AoneConfirmationModal.open(modalElement) => Promise<boolean>
 */
(function (win) {
  'use strict';

  var FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** @param {Element} container */
  function focusableList(container) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    return Array.prototype.filter.call(nodes, function (el) {
      return (
        el instanceof HTMLElement &&
        !el.hasAttribute('hidden') &&
        (el.offsetParent !== null || (el.getClientRects && el.getClientRects().length > 0))
      );
    });
  }

  /** @type {{ modal: HTMLElement|null, resolve: ((b: boolean) => void)|null, lastFocus: Element|null, tabHandler: ((e: KeyboardEvent) => void)|null, escHandler: ((e: KeyboardEvent) => void)|null, htmlOverflow: string, bodyOverflow: string }} */
  var state = {
    modal: null,
    resolve: null,
    lastFocus: null,
    tabHandler: null,
    escHandler: null,
    htmlOverflow: '',
    bodyOverflow: '',
  };

  /**
   * @param {HTMLElement} modalEl
   * @param {boolean} confirmed
   */
  function finish(modalEl, confirmed) {
    var dialog = modalEl.querySelector('[role="dialog"]');
    if (state.tabHandler && dialog) {
      dialog.removeEventListener('keydown', state.tabHandler);
    }
    state.tabHandler = null;
    if (state.escHandler) {
      document.removeEventListener('keydown', state.escHandler, true);
    }
    state.escHandler = null;

    var lock = modalEl.getAttribute('data-lock-body') !== 'false';
    if (lock) {
      document.documentElement.style.overflow = state.htmlOverflow;
      document.body.style.overflow = state.bodyOverflow;
    }

    modalEl.classList.remove('confirmation-modal--open');
    modalEl.setAttribute('hidden', '');
    modalEl.setAttribute('aria-hidden', 'true');

    var fn = state.resolve;
    var refocus = state.lastFocus;
    state.modal = null;
    state.resolve = null;
    state.lastFocus = null;

    if (fn) fn(confirmed);

    if (refocus && typeof refocus.focus === 'function') {
      try {
        refocus.focus();
      } catch (_) {}
    }
  }

  /**
   * @param {HTMLElement} modalEl
   * @returns {Promise<boolean>}
   */
  function open(modalEl) {
    return new Promise(function (resolve) {
      if (!(modalEl instanceof HTMLElement)) {
        resolve(false);
        return;
      }
      if (state.modal) {
        resolve(false);
        return;
      }

      state.resolve = resolve;
      state.lastFocus = document.activeElement;
      state.modal = modalEl;

      var lock = modalEl.getAttribute('data-lock-body') !== 'false';
      if (lock) {
        state.htmlOverflow = document.documentElement.style.overflow;
        state.bodyOverflow = document.body.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      }

      modalEl.removeAttribute('hidden');
      modalEl.setAttribute('aria-hidden', 'false');
      win.requestAnimationFrame(function () {
        modalEl.classList.add('confirmation-modal--open');
      });

      var dialog = modalEl.querySelector('[role="dialog"]');
      if (!dialog || !(dialog instanceof HTMLElement)) {
        finish(modalEl, false);
        return;
      }

      var ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
      /** @type {AddEventListenerOptions|boolean} */
      var lx = ac ? { signal: ac.signal } : false;

      var dismissOverlay = modalEl.getAttribute('data-overlay-dismiss') !== 'false';
      var overlay = modalEl.querySelector('.confirmation-modal__overlay');
      var cancelBtn = modalEl.querySelector('[data-confirmation-modal-cancel]');
      var closeBtn = modalEl.querySelector('[data-confirmation-modal-close]');
      var confirmBtn = modalEl.querySelector('[data-confirmation-modal-confirm]');

      function abortUiListeners() {
        if (ac) {
          try {
            ac.abort();
          } catch (_) {}
          return;
        }
        if (overlay) overlay.removeEventListener('click', onOverlayClick);
        if (cancelBtn instanceof HTMLElement) cancelBtn.removeEventListener('click', onCancelLegacy);
        if (closeBtn instanceof HTMLElement) closeBtn.removeEventListener('click', onCancelLegacy);
        if (confirmBtn instanceof HTMLElement) confirmBtn.removeEventListener('click', onConfirmLegacy);
      }

      function wrapFinish(confirmed) {
        abortUiListeners();
        finish(modalEl, confirmed);
      }

      function onOverlayClick(ev) {
        if (!dismissOverlay) return;
        if (ev.target === overlay) wrapFinish(false);
      }

      function onCancelLegacy() {
        wrapFinish(false);
      }

      function onConfirmLegacy() {
        wrapFinish(true);
      }

      if (overlay) {
        if (lx) overlay.addEventListener('click', onOverlayClick, lx);
        else overlay.addEventListener('click', onOverlayClick);
      }

      if (cancelBtn instanceof HTMLElement) {
        if (lx) cancelBtn.addEventListener('click', function () { wrapFinish(false); }, lx);
        else cancelBtn.addEventListener('click', onCancelLegacy);
      }
      if (closeBtn instanceof HTMLElement) {
        if (lx) closeBtn.addEventListener('click', function () { wrapFinish(false); }, lx);
        else closeBtn.addEventListener('click', onCancelLegacy);
      }
      if (confirmBtn instanceof HTMLElement) {
        if (lx) confirmBtn.addEventListener('click', function () { wrapFinish(true); }, lx);
        else confirmBtn.addEventListener('click', onConfirmLegacy);
      }

      state.tabHandler = function (e) {
        if (e.key !== 'Tab') return;
        var items = focusableList(dialog);
        if (items.length === 0) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      dialog.addEventListener('keydown', state.tabHandler);

      state.escHandler = function (e) {
        if (e.key !== 'Escape' || state.modal !== modalEl) return;
        e.preventDefault();
        wrapFinish(false);
      };
      document.addEventListener('keydown', state.escHandler, true);

      var focusPref = modalEl.querySelector('[data-confirmation-modal-cancel]');
      if (!(focusPref instanceof HTMLElement)) focusPref = modalEl.querySelector('[data-confirmation-modal-close]');
      var fb = focusableList(dialog)[0];
      var toFocus =
        focusPref instanceof HTMLElement ? focusPref : fb instanceof HTMLElement ? fb : dialog;
      try {
        toFocus.focus();
      } catch (_) {}
    });
  }

  win.AoneConfirmationModal = {
    open: open,
  };
})(window);
